import { useCallback } from "react";
import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { 
    CampaignClickThroughsQueryQuery, 
    QueryCampaignClickThroughsArgs
} from "../../../../../schemaTypes";

export const campaignClickThroughsQuery = gql`
  query CampaignClickThroughsQuery(
    $id: String!
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    campaignClickThroughs(
      id: $id
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
		data {
            id
            createdAt
            customer
        }
        total
        page
        pageSize
        totalPages
    }
  }
`;

type CampaignClickThroughItem = NonNullable<
  NonNullable<CampaignClickThroughsQueryQuery["campaignClickThroughs"]>["data"]
>[number];

export interface WithCampaignClickThroughs {
  data: CampaignClickThroughItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreClickThroughs: boolean;
}

interface Props {
  variables: QueryCampaignClickThroughsArgs;
  children: (data: WithCampaignClickThroughs) => React.ReactElement | null;
}

export const CampaignClickThroughsController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    CampaignClickThroughsQueryQuery,
    QueryCampaignClickThroughsArgs
  >(campaignClickThroughsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const campaignClickThroughsData = data?.campaignClickThroughs?.data || [];
  const normalizedClickThroughs: CampaignClickThroughItem[] = campaignClickThroughsData
    .filter((clickThrough): clickThrough is CampaignClickThroughItem => Boolean(clickThrough))
    .map((clickThrough) => ({
      ...clickThrough,
      customer: clickThrough?.customer || "",
      createdAt: clickThrough?.createdAt || "",
    }));
  const page = data?.campaignClickThroughs?.page || variables.page;
  const pageSize = data?.campaignClickThroughs?.pageSize || variables.pageSize;
  const total = data?.campaignClickThroughs?.total || 0;
  const totalPages = data?.campaignClickThroughs?.totalPages || 0;
  const hasMoreClickThroughs = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreClickThroughs) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.campaignClickThroughs) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          campaignClickThroughs: {
            ...fetchMoreResult?.campaignClickThroughs,
            data: [
              ...(prev?.campaignClickThroughs?.data || []),
              ...(fetchMoreResult?.campaignClickThroughs?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreClickThroughs]);

  return children({
    data: normalizedClickThroughs,
    loading,
    loadMore,
    hasMoreClickThroughs,
    page,
    pageSize,
    total,
    totalPages,
  });
};